// 1. React & Hooks
import { memo, useEffect, useRef, useState } from "react";

// 2. Helpers
import { useTranslate } from "helpers";

// 3. Forms & Validation
import { Errors } from "services/forms/validation/types";

// 4. API & Types
import {
  FleetTireSetEditFormValues,
} from "services/api/fleet/tires/types";
import { initialFleetTireSetEditForm } from "services/api/fleet/tires/consts";
import {
  createFleetTireSetAPI,
  getFleetTireSetAPI,
  publishFleetTireSetAPI,
  updateFleetTireSetAPI,
} from "services/api/fleet/tires/api";

// 5. UI Components
import WizardStep from "ui/components/Wizard/WizardStep";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import { Modal, SuccessOverlay } from "ui/components/Modal";
import TireSetIntro from "./components/TireSetIntro/TireSetIntro";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Loader } from "ui/components/Loader";
import successAnimation from "ui/theme/animations/success.json";

// 6. Local components
import TireRow from "./components/TireSetAddForm/components/TireRow/TireRow";
import TireSetLocationForm from "./components/TireSetLocationForm/TireSetLocationForm";
import TireSetDocumentForm from "./components/TireSetDocumentForm/TireSetDocumentForm";

// 7. Local types
import { TireSetEditProps } from "./types";
import { transformFleetTireSetEditForm } from "services/api/fleet/tires/transformations";
import { getFleetVehicleAPI } from "services/api/fleet/vehicles/api";
import { FleetVehicle } from "services/api/fleet/vehicles/types";


const TireSetEditCard: React.FC<TireSetEditProps> = (props) => {
  const { show, onSuccess, onClose, tireSetId, vehicleId, warehouseId, modalTitle, modalIcon, modalPosition } = props;

  const { t } = useTranslate();

  const [stepCurrent, setStepCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [formValues, setFormValues] = useState<FleetTireSetEditFormValues>(
    initialFleetTireSetEditForm
  );
  const [errors, setErrors] = useState<Errors>({});

  const stepValidators = useRef<Record<number, () => boolean | Promise<boolean>>>({});

  const isNewTireSet = !tireSetId;

  // Ovisi i o `show`, uz reset koraka: `stepCurrent` se inače vraćao na nulu samo nakon
  // uspješnog spremanja, pa bi prekinuti unos ostavio čarobnjak na zadnjem koraku.
  useEffect(() => {
    if (!show) return;

    setFormValues((prev) => ({
      ...initialFleetTireSetEditForm,
      tireSetId: tireSetId ?? prev.id,
    }));
    setStepCurrent(0);

    if (tireSetId) {
      fetchTireSet(tireSetId);
    }
  }, [show, tireSetId]);

  useEffect(() => {
    if (vehicleId) {
      fetchVehicle(vehicleId);
    }
  }, [vehicleId]);

  const fetchVehicle = async (id: string) => {
    if (id === undefined) {
      return;
    }

    setIsLoading(true);

    let result: FleetVehicle;
    try {
      result = await getFleetVehicleAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      vehicle: result,
    }));
    setIsLoading(false);

    setErrors({});
  };

  const fetchTireSet = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetTireSetEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getFleetTireSetAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    if (result) {
      setFormValues(transformFleetTireSetEditForm(result));
      setIsLoading(false);
    }

    setErrors({});
  };

  const handleNextStep = async () => {
    const stepValidator = stepValidators.current[stepCurrent];

    if (stepValidator) {
      const isValid = await stepValidator();
      if (!isValid) {
        return;
      }
    }

    await saveTireSetAsync();
    setStepCurrent((s) => s + 1);
  };

  const handleBackStep = async () => {
    await saveTireSetAsync();
    setStepCurrent((s) => s - 1);
  };

  const handleFinish = async () => {
    setIsLoading(true);
    await publishTireSet();
    setIsLoading(false);
  };

  const saveTireSetAsync = async () => {
    setIsLoading(true);

    try {
      let result;
      const id = tireSetId || formValues.id;

      if (!!id) {
        result = await updateFleetTireSetAPI(id, formValues, t);
      } else {
        result = await createFleetTireSetAPI(formValues, t);
      }
      if (result && onSuccess) {
        fetchTireSet(result.id);
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  }

  const publishTireSet = async () => {
    setIsLoading(true);
    try {
      let result;
      if (!!formValues.id) {
        result = await publishFleetTireSetAPI(formValues.id, t);
      }
      if (result && result.published) {
        setIsLoading(false);
        setShowSuccessOverlay(true);
        return;
      }
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  }

  const handleSuccessDismiss = () => {
    setShowSuccessOverlay(false);
    setFormValues(initialFleetTireSetEditForm);
    setStepCurrent(0);
    if (onSuccess) onSuccess();
  };

  const registerStepValidator = (
    step: number,
    validator: () => boolean | Promise<boolean>
  ) => {
    stepValidators.current[step] = validator;
  };

  const steps = [
    <WizardStep
      key={1}
      stepNumber={0}
      stepCurrent={stepCurrent}
    >
      <div className="flex items-center justify-center px-4 min-h-[70vh] 2xl:min-h-[1000px]">
        <div className="w-full  ">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1 mb-2">{t("admin.fleet.vehicle.tire.set.edit.tire.add.title")}</h1>
          <img src="/media/images/vehicles/tires.webp" alt="" className="h-[200px] mx-auto mb-4" />
          <p className="text-center text-md text-slate-500 mb-8">Dodajte gume koje će biti dio ovog seta.</p>
          <TireRow
            formValues={formValues}
            setFormValues={setFormValues}
            errors={errors}
            setErrors={setErrors}
            fetchTireSet={fetchTireSet}
            registerValidator={(fn) => registerStepValidator(stepCurrent, fn)}
          />
        </div>
      </div>
    </WizardStep>,
    <WizardStep
      key={2}
      stepNumber={1}
      stepCurrent={stepCurrent}
    >
      <div className="flex items-center justify-center px-4 min-h-[70vh] 2xl:min-h-[1000px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1 mb-2">Skladište</h1>
          <p className="text-center text-md text-slate-500 mb-8">Odaberite skladište na koje će se spremiti set guma.</p>
          <TireSetLocationForm
            formValues={formValues}
            setFormValues={setFormValues}
            errors={errors}
            setErrors={setErrors}
            registerValidator={(fn) => registerStepValidator(stepCurrent, fn)}
          />
        </div>
      </div>
    </WizardStep>,
    <WizardStep
      key={3}
      stepNumber={2}
      stepCurrent={stepCurrent}
    >
      <div className="flex items-center justify-center px-4 min-h-[70vh] 2xl:min-h-[1000px]">
        <div className="w-full max-w-4xl">
          <h1 className="text-2xl font-bold text-slate-900 text-center mb-1 mb-2">{t("admin.fleet.vehicle.tire.set.document.title")}</h1>
          <p className="text-center text-md text-slate-500 mb-8">Dodajte dokumentaciju vezanu uz set guma.</p>
          <TireSetDocumentForm
            formValues={formValues}
            setFormValues={setFormValues} />
        </div>
      </div>
    </WizardStep>,
  ];

  return (
    <>
      <SuccessOverlay
        show={showSuccessOverlay}
        animation={successAnimation}
        title="Set guma je uspješno kreiran!"
        description="Gume su dodane i lokacija je postavljena."
        onDismiss={handleSuccessDismiss}
      />
      <Modal
        show={show}
        onHide={onClose ?? (() => { })}
        position="side"
        size="full"
        intro={{
          content: (
            <TireSetIntro
              formValues={formValues}
              stepCurrent={stepCurrent}
              stepCount={steps.length}
              isNewTireSet={isNewTireSet}
            />
          ),
        }}
      >
        <ModalHeader
          icon={modalIcon}
          title={modalTitle}
          onHide={onClose ?? (() => { })}
        />
        <ModalBody>
          {isLoading && (
            <div className="relative h-[calc(100vh-80px)]">
              <Loader isLoading={true}>{null}</Loader>
            </div>
          )}
          <div className={isLoading ? "hidden" : ""}>
            <Wizard steps={steps} />
          </div>
        </ModalBody>
        <WizardFooter
          stepCurrent={stepCurrent}
          steps={steps}
          onNextStep={handleNextStep}
          onBackStep={handleBackStep}
          onFinish={handleFinish}
          isLoading={isLoading}
          isReadOnly={false}
        />
      </Modal>
    </>
  );
};
export default memo(TireSetEditCard);
